/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Flex,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Controller,
  useController,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { AiOutlineDelete } from "react-icons/ai";
import { IoMdAddCircle } from "react-icons/io";
import { MdOutlineAdd } from "react-icons/md";
import InputMask from "react-input-mask";
import Select from "react-select";
import countryList from "react-select-country-list";
import scus from "state-counties-us";
import {
  addressTypeOptions,
  usStateOptions,
} from "../../../utils/case/usStateOption";
import { selectStyles } from "../../../utils/selectStyel";
const addressFields = [
  "TYPE",
  "PRIMARY",
  "STREET_1",
  "STREET_2",
  "CITY",
  "STATE",
  "COUNTY",
  "ZIP",
  "COUNTRY",
  "EMAIL_ADDRESS",
  "TELEPHONE_HOME",
  "TELEPHONE_MOBILE",
  "TELEPHONE_WORK",
  "START_DATE",
  "END_DATE",
];

const EditAddressForm = ({ initialData = [], onChange }) => {
  const countryOptions = useMemo(() => countryList().getData(), []);
  const [countyOptionsMap, setCountyOptionsMap] = useState({});
  const toast = useToast();
  const [suggestedAddresses, setSuggestedAddresses] = useState({});
  const [verifyingIndexes, setVerifyingIndexes] = useState([]);
  const skipVerificationRef = useRef(new Set());
  const prevVerifiableFields = useRef({});

  const { control, register, setValue, watch } = useForm({
    defaultValues: {
      addresses: initialData.length > 0 ? initialData : [getDefaultAddress()],
    },
  });
  const addresses = useWatch({ name: "addresses", control });
  const { fields, remove, prepend } = useFieldArray({
    control,
    name: "addresses",
  });

  const watchedAddresses = watch("addresses");
  useEffect(() => {
    onChange?.(watchedAddresses);
  }, [watchedAddresses]);

  useEffect(() => {
    if (initialData.length > 0) {
      initialData.forEach((address, index) => {
        addressFields.forEach((fieldName) => {
          const value = address[fieldName];

          if (fieldName.includes("DATE")) {
            const formattedDate = moment(
              value,
              ["YYYYMMDD", "YYYY-MM-DD"],
              true
            );
            setValue(
              `addresses.${index}.${fieldName}`,
              formattedDate.isValid()
                ? formattedDate.format("YYYY-MM-DD")
                : null
            );
          } else if (fieldName === "TELEPHONE_WORK" && value?.includes("x")) {
            const [main, ext] = value.split("x").map((s) => s.trim());
            setValue(`addresses.${index}.TELEPHONE_WORK`, main);
            setValue(`addresses.${index}.TELEPHONE_WORK_EXT`, ext);
          } else {
            setValue(`addresses.${index}.${fieldName}`, value ?? "");
          }
        });
      });
    }
  }, [initialData, setValue]);

  useEffect(() => {
    const timeouts = [];

    addresses?.forEach((addr, index) => {
      const { STREET_1, STREET_2, CITY, STATE, ZIP } = addr || {};
      const isVerifiable = STREET_1 && STATE && (CITY || ZIP);

      const prev = prevVerifiableFields.current[index] || {};
      const hasChanged =
        prev.STREET_1 !== STREET_1 ||
        prev.CITY !== CITY ||
        prev.STATE !== STATE ||
        prev.ZIP !== ZIP;

      if (
        !isVerifiable ||
        !hasChanged ||
        skipVerificationRef.current.has(index)
      ) {
        skipVerificationRef.current.delete(index);
        return;
      }

      // Store current values for future comparison
      prevVerifiableFields.current[index] = {
        STREET_1,
        STREET_2,
        CITY,
        STATE,
        ZIP,
      };

      const timeout = setTimeout(() => {
        verifyAddress(index);
      }, 1000);
      timeouts.push(timeout);
    });

    return () => timeouts.forEach(clearTimeout);
  }, [addresses]);

  const verifyAddress = async (index) => {
    setVerifyingIndexes((prev) => [...prev, index]);
    const values = addresses[index];
    const { STREET_1, CITY, STATE, ZIP, STREET_2 } = values;
    const query = `STREET1=${
      STREET_1?.trim().replace(/\s+/g, "+") || ""
    }&STREET2=${STREET_2?.trim().replace(/\s+/g, "+") || ""}&CITY=${
      CITY?.trim().replace(/\s+/g, "+") || ""
    }&STATE=${STATE || ""}&ZIP=${ZIP || ""}`;
    const url = `/api/getAddress?${query}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "text/plain; charset=utf-8",
        },
      });

      const text = await response.text();
      console.log("Server response:", text);

      if (!response.ok) {
        if (response.status === 400) {
          // Show raw server message in UI (no toast)
          setSuggestedAddresses((prev) => ({
            ...prev,
            [index]: { error: text },
          }));
        } else {
          throw new Error(text || "Failed to verify address");
        }
        return;
      }
      const data = JSON.parse(text);
      setSuggestedAddresses((prev) => ({
        ...prev,
        [index]: data,
      }));
    } catch (err) {
      console.error(err);
      toast({
        title: "Verification Failed",
        description: "Unable to verify address. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setVerifyingIndexes((prev) => prev.filter((i) => i !== index));
    }
  };
  const handleUseSuggested = (index) => {
    const suggestion = suggestedAddresses[index];
    if (!suggestion) return;
    // Skip triggering verification again
    skipVerificationRef.current.add(index);
    setValue(`addresses.${index}.STREET_1`, suggestion.STREET1 || "");
    setValue(`addresses.${index}.STREET_2`, suggestion.STREET2 || "");
    setValue(`addresses.${index}.CITY`, suggestion.CITY || "");
    setValue(`addresses.${index}.STATE`, suggestion.STATE || "");
    setValue(`addresses.${index}.ZIP`, suggestion.ZIP || "");
    setSuggestedAddresses((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  useEffect(() => {
    addresses?.forEach((addr, index) => {
      const stateCode = addr?.STATE;
      if (stateCode && !countyOptionsMap[stateCode]) {
        const counties = scus.getCountiesByState(stateCode) || [];
        setCountyOptionsMap((prev) => ({
          ...prev,
          [stateCode]: counties.map((c) => ({
            value: `${c} County`,
            label: `${c} County`,
          })),
        }));
      }
    });
  }, [addresses]);

  return (
    <Box>
      <Flex alignItems="center" gap={2} mb={4}>
        <Text fontWeight={600}>Addresses</Text>
        <IoMdAddCircle
          fontSize="20px"
          cursor="pointer"
          onClick={() => prepend(getDefaultAddress())}
        />
      </Flex>

      {fields.map((field, index) => (
        <Box key={field.id} mb={4} border="1px solid gray" p={4}>
          <Flex
            alignItems="center"
            gap={2}
            mb={4}
            w="100%"
            justifyContent="space-between"
          >
            <Text fontWeight={600}>Address {index + 1}</Text>
            <Box display="flex" alignItems="center" gap={2}>
              <AiOutlineDelete
                fontSize="20px"
                cursor="pointer"
                onClick={() => remove(index)}
                color="red"
              />
            </Box>
          </Flex>

          <SimpleGrid columns={[1, 2, 3]} spacing={4}>
            {addressFields.map((fieldName) => (
              <Box key={fieldName}>
                <Text fontSize="sm" mb={1}>
                  {fieldName.replace(/_/g, " ")}
                </Text>

                {fieldName.includes("DATE") ? (
                  <Input
                    type="date"
                    borderRadius="sm"
                    {...register(`addresses.${index}.${fieldName}`, {
                      setValueAs: (v) => (v === "" ? null : v),
                    })}
                    defaultValue={getValidDate(field[fieldName])}
                  />
                ) : fieldName.includes("TELEPHONE") ? (
                  <Box display="flex" gap={1}>
                    <PhoneMaskedInput
                      control={control}
                      name={`addresses.${index}.${fieldName}`}
                      defaultValue={field[fieldName] || ""}
                      mask={"(999) 999-9999"}
                    />
                    {fieldName === "TELEPHONE_WORK" && (
                      <Box>
                        <Input
                          borderRadius="sm"
                          w="90px"
                          placeholder="Ext"
                          {...register(
                            `addresses.${index}.TELEPHONE_WORK_EXT`,
                            {
                              setValueAs: (v) => (v === "" ? null : v),
                            }
                          )}
                        />
                      </Box>
                    )}
                  </Box>
                ) : fieldName === "STATE" ? (
                  <Controller
                    control={control}
                    name={`addresses.${index}.STATE`}
                    render={({ field: selectField }) => (
                      <Select
                        {...selectField}
                        styles={selectStyles}
                        options={usStateOptions}
                        placeholder="Select State"
                        isClearable
                        value={
                          usStateOptions.find(
                            (opt) => opt.value === selectField.value
                          ) || null
                        }
                        onChange={(val) =>
                          selectField.onChange(val ? val.value : null)
                        }
                      />
                    )}
                  />
                ) : fieldName === "COUNTY" ? (
                  (() => {
                    const selectedState = addresses?.[index]?.STATE;
                    const countyOptions = countyOptionsMap[selectedState] || [];

                    return (
                      <Controller
                        control={control}
                        name={`addresses.${index}.COUNTY`}
                        render={({ field: selectField }) => (
                          <Select
                            {...selectField}
                            styles={selectStyles}
                            options={countyOptions}
                            placeholder="Select County"
                            isClearable
                            value={
                              countyOptions.find(
                                (opt) =>
                                  opt.value === addresses?.[index]?.COUNTY
                              ) || null
                            }
                            onChange={(selected) =>
                              setValue(
                                `addresses.${index}.COUNTY`,
                                selected?.value || ""
                              )
                            }
                          />
                        )}
                      />
                    );
                  })()
                ) : fieldName === "TYPE" ? (
                  <Controller
                    control={control}
                    name={`addresses.${index}.TYPE`}
                    render={({ field: selectField }) => (
                      <Select
                        {...selectField}
                        styles={selectStyles}
                        options={addressTypeOptions}
                        placeholder="Select Type"
                        isClearable
                        value={
                          addressTypeOptions.find(
                            (opt) => opt.value === selectField.value
                          ) || null
                        }
                        onChange={(val) =>
                          selectField.onChange(val ? val.value : null)
                        }
                      />
                    )}
                  />
                ) : fieldName === "COUNTRY" ? (
                  <Controller
                    control={control}
                    name={`addresses.${index}.COUNTRY`}
                    render={({ field: selectField }) => (
                      <Select
                        {...selectField}
                        styles={selectStyles}
                        options={countryOptions}
                        placeholder="Select Country"
                        isClearable
                        value={
                          countryOptions.find(
                            (opt) => opt.value === selectField.value
                          ) || null
                        }
                        onChange={(val) =>
                          selectField.onChange(val ? val.value : null)
                        }
                      />
                    )}
                  />
                ) : (
                  <Input
                    borderRadius="sm"
                    type="text"
                    {...register(`addresses.${index}.${fieldName}`, {
                      setValueAs: (v) => (v === "" ? null : v),
                    })}
                    defaultValue={field[fieldName] || ""}
                  />
                )}
              </Box>
            ))}
          </SimpleGrid>
          {verifyingIndexes.includes(index) ? (
            <Flex mt={3} justify="center">
              <Spinner size="sm" />
            </Flex>
          ) : suggestedAddresses[index] ? (
            suggestedAddresses[index].error ? (
              // Show error box for 400 status
              <Box
                mt={2}
                p={2}
                bg="red.50"
                border="1px solid"
                borderColor="red.300"
                borderRadius="md"
              >
                <Text
                  fontSize="sm"
                  color="red.600"
                  fontWeight="medium"
                  textAlign="center"
                >
                  {suggestedAddresses[index].error}
                </Text>
              </Box>
            ) : (
              // Show suggested address box
              <Box
                mt={2}
                p={2}
                bg="gray.100"
                borderRadius="md"
                display="flex"
                alignItems="center"
                gap={2}
                justifyContent="space-between"
              >
                <Box>
                  <Text fontSize="sm" fontWeight="bold">
                    Suggested Address:
                  </Text>
                  <Text fontSize="sm">
                    {suggestedAddresses[index].STREET1},{" "}
                    {suggestedAddresses[index].STREET2},{" "}
                    {suggestedAddresses[index].CITY},{" "}
                    {suggestedAddresses[index].STATE}{" "}
                    {suggestedAddresses[index].ZIP}
                  </Text>
                </Box>
                <MdOutlineAdd
                  fontWeight={600}
                  fontSize={32}
                  cursor="pointer"
                  onClick={() => handleUseSuggested(index)}
                />
              </Box>
            )
          ) : null}
        </Box>
      ))}
    </Box>
  );
};

export default EditAddressForm;
const getDefaultAddress = () => {
  const currentDate = moment().format("YYYY-MM-DD");

  return addressFields.reduce((acc, key) => {
    switch (key) {
      case "PRIMARY":
        acc[key] = true;
        break;
      case "START_DATE":
        acc[key] = currentDate;
        break;
      case "COUNTRY":
        acc[key] = "US";
        break;
      default:
        acc[key] = "";
    }
    return acc;
  }, {});
};

const getValidDate = (dateString) => {
  const m = moment(dateString, ["YYYYMMDD", "YYYY-MM-DD"], true);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

const PhoneMaskedInput = ({ control, name, defaultValue, mask }) => {
  const {
    field: { onChange, onBlur, value, ref },
  } = useController({
    name,
    control,
    defaultValue,
  });

  return (
    <InputMask
      mask={mask}
      maskChar={null}
      value={value || ""}
      onChange={onChange}
      onBlur={onBlur}
    >
      {(inputProps) => <Input borderRadius="sm" {...inputProps} ref={ref} />}
    </InputMask>
  );
};
